import { useMemo, useState, type FormEvent } from 'react';
import { Button, Input, Modal, Select, type SelectOption } from '../../components';
import { ApiError } from '../../lib/api';
import {
  COUNTRIES,
  DEFAULT_COUNTRY_NAME,
  DEFAULT_DIAL_CODE,
  joinPhone,
  splitPhone,
} from '../../lib/countries';
import type {
  LeadCreatePayload,
  LeadRow,
  LeadUpdatePayload,
  LookupOption,
  UserOption,
} from './types';

type FormState = {
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone_dial: string;
  phone_local: string;
  company: string;
  country: string;
  linkedin_url: string;
  received_reference: string;
  notes: string;
  date_of_birth: string;
  entry_date: string;
  lead_status_id: string;
  lead_category_id: string;
  source_id: string;
  assigned_sales_user_id: string;
  assigned_pse_user_id: string;
};

function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toFormState(row?: LeadRow): FormState {
  const phone = splitPhone(row?.phone);
  return {
    first_name: row?.first_name ?? '',
    last_name: row?.last_name ?? '',
    full_name: row?.full_name ?? '',
    email: row?.email ?? '',
    phone_dial: phone.dial,
    phone_local: phone.local,
    company: row?.company ?? '',
    country: row?.country ?? (row ? '' : DEFAULT_COUNTRY_NAME),
    linkedin_url: row?.linkedin_url ?? '',
    received_reference: row?.received_reference ?? '',
    notes: row?.notes ?? '',
    date_of_birth: row?.date_of_birth ?? '',
    entry_date: row?.entry_date ?? (row ? '' : todayISO()),
    lead_status_id: row?.lead_status_id ?? '',
    lead_category_id: row?.lead_category_id ?? '',
    source_id: row?.source_id ?? '',
    assigned_sales_user_id: row?.assigned_sales_user_id ?? '',
    assigned_pse_user_id: row?.assigned_pse_user_id ?? '',
  };
}

function toPayload(form: FormState): LeadCreatePayload {
  const s = (v: string) => (v.trim() === '' ? null : v.trim());
  const nameGuess =
    s(form.full_name) ??
    `${form.first_name.trim()} ${form.last_name.trim()}`.trim() ??
    '';
  return {
    first_name: s(form.first_name),
    last_name: s(form.last_name),
    full_name: nameGuess || 'Unnamed lead',
    email: s(form.email),
    phone: joinPhone(form.phone_dial, form.phone_local),
    company: s(form.company),
    country: s(form.country),
    linkedin_url: s(form.linkedin_url),
    received_reference: s(form.received_reference),
    notes: s(form.notes),
    date_of_birth: s(form.date_of_birth),
    entry_date: s(form.entry_date),
    lead_status_id: s(form.lead_status_id),
    lead_category_id: s(form.lead_category_id),
    source_id: s(form.source_id),
    assigned_sales_user_id: s(form.assigned_sales_user_id),
    assigned_pse_user_id: s(form.assigned_pse_user_id),
  };
}

export function LeadFormModal({
  row,
  statuses,
  categories,
  sources,
  users,
  onClose,
  onSubmit,
}: {
  row?: LeadRow;
  statuses: LookupOption[];
  categories: LookupOption[];
  sources: LookupOption[];
  users: UserOption[];
  onClose: () => void;
  onSubmit: (payload: LeadCreatePayload | LeadUpdatePayload) => Promise<void>;
}) {
  const [form, setForm] = useState<FormState>(() => toFormState(row));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statusOpts: SelectOption[] = statuses.map((s) => ({ value: s.id, label: s.name }));
  const categoryOpts: SelectOption[] = categories.map((c) => ({ value: c.id, label: c.name }));
  const sourceOpts: SelectOption[] = sources.map((s) => ({ value: s.id, label: s.name }));
  const userOpts: SelectOption[] = users.map((u) => ({ value: u.id, label: u.full_name }));

  const dialOpts: SelectOption[] = useMemo(() => {
    // Deduplicate dial codes: shared codes (e.g. +1 USA/Canada) collapse into one option.
    const byDial = new Map<string, string[]>();
    for (const c of COUNTRIES) {
      const arr = byDial.get(c.dial) ?? [];
      arr.push(c.iso2);
      byDial.set(c.dial, arr);
    }
    return Array.from(byDial.entries()).map(([dial, isos]) => ({
      value: dial,
      label: `${dial} (${isos.join(', ')})`,
    }));
  }, []);

  const countryOpts: SelectOption[] = useMemo(
    () => COUNTRIES.map((c) => ({ value: c.name, label: c.name })),
    [],
  );

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(toPayload(form));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit lead' : 'New lead'} width={640}>
      <form onSubmit={handleSubmit}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="lead-first"
                label="First name"
                value={form.first_name}
                onChange={(v) => setForm({ ...form, first_name: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id="lead-last"
                label="Last name"
                value={form.last_name}
                onChange={(v) => setForm({ ...form, last_name: v })}
              />
            </div>
          </div>

          <Input
            id="lead-fullname"
            label="Display name"
            value={form.full_name}
            onChange={(v) => setForm({ ...form, full_name: v })}
            required
            autoFocus
          />

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="lead-email"
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="field-label" htmlFor="lead-phone">
                Phone
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: '0 0 130px' }}>
                  <Select
                    id="lead-phone-dial"
                    aria-label="Country code"
                    value={form.phone_dial}
                    options={dialOpts}
                    onChange={(v) => setForm({ ...form, phone_dial: v || DEFAULT_DIAL_CODE })}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Input
                    id="lead-phone"
                    type="tel"
                    inputMode="tel"
                    placeholder="Phone number"
                    value={form.phone_local}
                    onChange={(v) => setForm({ ...form, phone_local: v })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="lead-company"
                label="Company"
                value={form.company}
                onChange={(v) => setForm({ ...form, company: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                id="lead-country"
                label="Country"
                placeholder="— select country —"
                value={form.country}
                options={countryOpts}
                onChange={(v) => setForm({ ...form, country: v })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="lead-linkedin"
                label="LinkedIn URL"
                value={form.linkedin_url}
                onChange={(v) => setForm({ ...form, linkedin_url: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id="lead-ref"
                label="Received reference"
                value={form.received_reference}
                onChange={(v) => setForm({ ...form, received_reference: v })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="lead-dob"
                label="Date of birth"
                type="date"
                value={form.date_of_birth}
                onChange={(v) => setForm({ ...form, date_of_birth: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                id="lead-entry"
                label="Entry date"
                type="date"
                value={form.entry_date}
                onChange={(v) => setForm({ ...form, entry_date: v })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Select
                id="lead-status"
                label="Status"
                placeholder="— none —"
                value={form.lead_status_id}
                options={statusOpts}
                onChange={(v) => setForm({ ...form, lead_status_id: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                id="lead-category"
                label="Category"
                placeholder="— none —"
                value={form.lead_category_id}
                options={categoryOpts}
                onChange={(v) => setForm({ ...form, lead_category_id: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                id="lead-source"
                label="Source"
                placeholder="— none —"
                value={form.source_id}
                options={sourceOpts}
                onChange={(v) => setForm({ ...form, source_id: v })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Select
                id="lead-sales"
                label="Assigned sales"
                placeholder="— unassigned —"
                value={form.assigned_sales_user_id}
                options={userOpts}
                onChange={(v) => setForm({ ...form, assigned_sales_user_id: v })}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                id="lead-pse"
                label="Assigned PSE (pre-sales person)"
                placeholder="— unassigned —"
                value={form.assigned_pse_user_id}
                options={userOpts}
                onChange={(v) => setForm({ ...form, assigned_pse_user_id: v })}
              />
            </div>
          </div>

          <div>
            <label className="field-label" htmlFor="lead-notes">
              Notes
            </label>
            <textarea
              id="lead-notes"
              className="input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
            />
          </div>

          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{
            justifyContent: 'flex-end',
            borderTop: '1px solid var(--border)',
            borderBottom: 'none',
          }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
